/**
 * A partner code (docs/partners.md): four characters, A to Z and 2 to 9, the same rule as the API,
 * CapturePoint and Phone Home. Case, spaces and dashes do not matter, and a typed 0 or 1 reads as
 * O or I, since codes never use them. Null when what was typed cannot be a code.
 */
export function normalizePartnerCode(typed: string): string | null {
  // ASCII only, before any case mapping, so the browser reads every input as the API does.
  if (typed.length > 32 || !/^[\x20-\x7e\t]*$/.test(typed)) return null;
  const code = typed
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '')
    .replace(/0/g, 'O')
    .replace(/1/g, 'I');
  return /^[A-Z2-9]{4}$/.test(code) ? code : null;
}

/** The one line for a code that is not a partner's, whether it was mistyped or not a code at all. */
export const notRecognized = "That partner code isn't recognized. Check it, or leave it empty.";

const KEY = 'paige.partner';

/** How long a partner's link is remembered (Dustin, 2026-09-28). */
export const linkDays = 90;

interface StoredLink {
  code: string;
  at: string;
}

/**
 * A partner's link (`?partner=MILN` on any Paige page) is remembered so sign-up can offer the code:
 * the latest link wins, and it is forgotten after {@link linkDays} days.
 */
export function capturePartnerLink(now: Date = new Date()): void {
  try {
    const code = normalizePartnerCode(
      new URL(window.location.href).searchParams.get('partner') ?? '',
    );
    if (code) localStorage.setItem(KEY, JSON.stringify({ code, at: now.toISOString() }));
  } catch {
    /* a private window keeps nothing; the code can still be typed */
  }
}

export function storedPartnerLink(now: Date = new Date()): string | null {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? 'null') as StoredLink | null;
    const age = stored ? now.getTime() - Date.parse(stored.at) : Number.NaN;
    if (!stored || !(age >= 0 && age <= linkDays * 86_400_000)) return null;
    return normalizePartnerCode(stored.code);
  } catch {
    return null;
  }
}

/** After sign-up carried it: the partner belongs to the company now. */
export function clearPartnerLink(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
